"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import {
  ChevronDown,
  MessageCircle,
  X,
  Award,
  Users,
  BookOpen,
  Shield,
  CheckCircle,
  GraduationCap,
  Building,
  Briefcase,
  Globe,
  Clock,
  TrendingUp,
} from "lucide-react"

export default function CoursesPage() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleChatSubmit = (e) => {
    e.preventDefault()
    console.log("Chat form submitted")
  }

  const courses = [
    {
      id: "etabs",
      title: "ETABS for RCC Structures",
      description: "Master building analysis and design using ETABS with real-world G+3/G+5 projects.",
      duration: "6 weeks",
      level: "Intermediate",
      icon: Building,
      features: ["Real-world Projects", "G+3/G+5 Buildings", "Analysis & Design", "Industry Standards"],
    },
    {
      id: "staadpro",
      title: "STAAD.Pro for Steel Design",
      description: "Comprehensive training in STAAD.Pro with steel structure modeling and detailing.",
      duration: "8 weeks",
      level: "Beginner to Advanced",
      icon: Briefcase,
      features: ["Steel Modeling", "Structure Analysis", "Detailing", "Code Compliance"],
    },
    {
      id: "autocad",
      title: "AutoCAD for Structural Drafting",
      description: "Learn how to draft structural plans, reinforcement details, and site layouts in AutoCAD.",
      duration: "4 weeks",
      level: "Beginner",
      icon: BookOpen,
      features: ["Structural Plans", "Reinforcement Details", "Site Layouts", "2D Drafting"],
    },
    {
      id: "safe",
      title: "SAFE for Foundation Design",
      description: "Learn slab and foundation design using SAFE with hands-on examples and case studies.",
      duration: "5 weeks",
      level: "Intermediate",
      icon: Shield,
      features: ["Slab Design", "Foundation Design", "Case Studies", "Hands-on Training"],
    },
    {
      id: "tekla",
      title: "Tekla Structures",
      description: "Train in Tekla for steel detailing and BIM workflows with industry-standard practices.",
      duration: "10 weeks",
      level: "Advanced",
      icon: Globe,
      features: ["Steel Detailing", "BIM Workflows", "3D Modeling", "Industry Practices"],
    },
    {
      id: "manual",
      title: "Manual RCC & Steel Design",
      description: "Learn to manually calculate beam, slab, column & footing designs using IS 456 & IS 800.",
      duration: "12 weeks",
      level: "Beginner to Advanced",
      icon: GraduationCap,
      features: ["Manual Calculations", "IS 456 & IS 800", "Design Principles", "Structural Elements"],
    },
  ]

  const footerLinks = [
    { to: "/", label: "HOME" },
    { to: "/psdct", label: "PSDCT LIVE MODE" },
    { to: "/terms", label: "TERM & CONDITIONS" },
    { to: "/pay", label: "QUICK PAY ANY FEES" },
    { to: "/registration-policy", label: "REGISTRATION POLICY" },
    { to: "/cancellation", label: "CANCELLATION POLICY" },
    { to: "/careers", label: "CAREERS" },
    { to: "/contact", label: "CONTACT US" },
    { to: "/franchise", label: "FRANCHISE" },
    { to: "/free", label: "FREE" },
    { to: "/students", label: "STUDENTS" },
    { to: "/training", label: "TRAINING CENTERS" },
  ]

  return (
    <div className="bg-white text-gray-800 min-h-screen overflow-x-hidden">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-white/20 backdrop-blur-2xl border-b border-white/10 shadow-xl"
            : "bg-gray-900/30 backdrop-blur-xl text-white border-b border-white/5"
        }`}
      >
        <div className="container mx-auto flex justify-between items-center p-4">
          <div
            className={`text-2xl font-bold transition-all duration-500 ${isScrolled ? "text-[#06b6d4]" : "text-white"}`}
          >
            <Link to="/" className="relative inline-block">
              <span className="bg-gradient-to-r from-[#00d4ff] via-[#5b21b6] to-[#ec4899] bg-clip-text text-transparent">
                StructuralGuru
              </span>
              <div className="absolute -inset-3 bg-gradient-to-r from-[#00d4ff]/20 via-[#5b21b6]/20 to-[#ec4899]/20 rounded-2xl blur-2xl opacity-50 animate-pulse"></div>
              <div className="absolute -inset-4 bg-gradient-to-r from-[#00d4ff]/15 via-[#5b21b6]/15 to-[#ec4899]/15 rounded-3xl blur-3xl opacity-30 animate-pulse"></div>
            </Link>
          </div>
          <nav className="hidden md:flex space-x-6">
            <Link
              to="/"
              className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
              } ${
                isScrolled
                  ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                  : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
              } rounded-lg px-2 py-1 overflow-hidden after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full`}
            >
              HOME
            </Link>
            <Link
              to="/about"
              className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
              } ${
                isScrolled
                  ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                  : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
              } rounded-lg px-2 py-1 overflow-hidden after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full`}
            >
              ABOUT US
            </Link>
            <Link
              to="/services"
              className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
              } ${
                isScrolled
                  ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                  : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
              } rounded-lg px-2 py-1 overflow-hidden after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full`}
            >
              SERVICES
            </Link>
            <Link
              to="/projects"
              className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
              } ${
                isScrolled
                  ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                  : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
              } rounded-lg px-2 py-1 overflow-hidden after:content-[''] after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full`}
            >
              OUR ENGG PROJECTS
            </Link>
            <div className="relative">
              <button
                onClick={() => setIsMoreDropdownOpen(!isMoreDropdownOpen)}
                className={`flex items-center space-x-1 font-medium transition-all duration-300 hover:scale-105 ${
                  isScrolled ? "text-gray-700 hover:text-[#0f766e]" : "text-white hover:text-gray-200"
                } ${
                  isScrolled
                    ? "hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] hover:border hover:border-[#06b6d4]/50"
                    : "hover:shadow-[0_0_15px_rgba(255,255,255,0.4)] hover:border hover:border-white/50"
                } rounded-lg px-2 py-1`}
                aria-expanded={isMoreDropdownOpen}
                aria-haspopup="true"
              >
                <span>MORE</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${isMoreDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`absolute top-full right-0 mt-2 w-48 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/20 overflow-hidden transition-all duration-300 ${
                  isMoreDropdownOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-4 invisible"
                }`}
              >
                <Link
                  to="/privacy"
                  className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                  onClick={() => setIsMoreDropdownOpen(false)}
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/quality"
                  className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                  onClick={() => setIsMoreDropdownOpen(false)}
                >
                  Quality Policy
                </Link>
                <Link
                  to="/terms"
                  className="block px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-[#0f766e] transition-all duration-200"
                  onClick={() => setIsMoreDropdownOpen(false)}
                >
                  Terms & Conditions
                </Link>
              </div>
            </div>
          </nav>
          {/* Mobile menu button */}
          <button className="md:hidden text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 bg-gray-50 relative overflow-hidden pt-20">
        {/* Enhanced Vibrant Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Large gradient orbs */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-[#06b6d4]/30 to-[#8b5cf6]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-l from-[#7c3aed]/25 to-[#06b6d4]/15 rounded-full blur-3xl animate-pulse"></div>
          {/* Additional colorful floating elements */}
          <div
            className="absolute top-1/4 right-1/4 w-64 h-64 bg-gradient-to-br from-[#f59e0b]/20 to-[#ef4444]/15 rounded-full blur-2xl animate-bounce"
            style={{ animationDuration: "6s" }}
          ></div>
          <div
            className="absolute bottom-1/3 left-1/4 w-48 h-48 bg-gradient-to-tr from-[#10b981]/25 to-[#3b82f6]/20 rounded-full blur-2xl animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>
          {/* Floating geometric shapes */}
          <div
            className="absolute top-1/2 left-1/3 w-32 h-32 bg-gradient-to-r from-[#ec4899]/20 to-[#8b5cf6]/15 rounded-full blur-xl animate-ping"
            style={{ animationDuration: "4s" }}
          ></div>
          <div
            className="absolute top-3/4 right-1/3 w-40 h-40 bg-gradient-to-bl from-[#06b6d4]/25 to-[#10b981]/20 rounded-full blur-xl animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
          {/* Moving gradient streaks */}
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-[#06b6d4]/30 to-transparent animate-pulse"></div>
          <div
            className="absolute bottom-0 right-0 w-full h-2 bg-gradient-to-l from-transparent via-[#7c3aed]/30 to-transparent animate-pulse"
            style={{ animationDelay: "3s" }}
          ></div>
          {/* Rotating elements */}
          <div
            className="absolute top-1/3 left-1/2 w-24 h-24 bg-gradient-to-r from-[#f59e0b]/30 to-[#ef4444]/25 rounded-full blur-lg animate-spin"
            style={{ animationDuration: "20s" }}
          ></div>
          <div
            className="absolute bottom-1/2 right-1/2 w-36 h-36 bg-gradient-to-l from-[#10b981]/20 to-[#06b6d4]/25 rounded-full blur-lg animate-spin"
            style={{ animationDuration: "25s", animationDirection: "reverse" }}
          ></div>
        </div>

        {/* Hero Section */}
        <section className="max-w-6xl mx-auto text-center py-16 relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#0f766e] via-[#7c3aed] to-[#0f766e] bg-clip-text text-transparent">
              Our Popular Courses
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed">
            Master structural engineering with our comprehensive courses designed by industry experts. Get hands-on
            experience with the latest software and methodologies.
          </p>

          {/* Stats Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg border border-white/20 transform hover:scale-105 transition-all duration-300 hover:shadow-2xl">
              <div className="flex items-center justify-center mb-2">
                <Users className="w-8 h-8 text-[#0f766e]" />
              </div>
              <div className="text-2xl font-bold text-[#0f766e]">5000+</div>
              <div className="text-sm text-gray-600">Students Trained</div>
            </div>
            <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg border border-white/20 transform hover:scale-105 transition-all duration-300 hover:shadow-2xl">
              <div className="flex items-center justify-center mb-2">
                <Award className="w-8 h-8 text-[#7c3aed]" />
              </div>
              <div className="text-2xl font-bold text-[#7c3aed]">15+</div>
              <div className="text-sm text-gray-600">Years Experience</div>
            </div>
            <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg border border-white/20 transform hover:scale-105 transition-all duration-300 hover:shadow-2xl">
              <div className="flex items-center justify-center mb-2">
                <Building className="w-8 h-8 text-[#06b6d4]" />
              </div>
              <div className="text-2xl font-bold text-[#06b6d4]">95%</div>
              <div className="text-sm text-gray-600">Placement Rate</div>
            </div>
            <div className="bg-white/60 backdrop-blur-xl p-6 rounded-2xl shadow-lg border border-white/20 transform hover:scale-105 transition-all duration-300 hover:shadow-2xl">
              <div className="flex items-center justify-center mb-2">
                <Globe className="w-8 h-8 text-[#0f766e]" />
              </div>
              <div className="text-2xl font-bold text-[#0f766e]">50+</div>
              <div className="text-sm text-gray-600">Cities Covered</div>
            </div>
          </div>

          {/* Certifications */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            <span className="inline-flex items-center px-6 py-3 bg-white/60 backdrop-blur-sm border border-[#0f766e]/30 rounded-full text-sm font-medium transform hover:scale-105 transition-all duration-300 hover:shadow-lg">
              <Shield className="w-4 h-4 mr-2" />
              ISO 9001:2015 Certified
            </span>
            <span className="inline-flex items-center px-6 py-3 bg-white/60 backdrop-blur-sm border border-[#7c3aed]/30 rounded-full text-sm font-medium transform hover:scale-105 transition-all duration-300 hover:shadow-lg">
              <Award className="w-4 h-4 mr-2" />
              AICTE Approved
            </span>
            <span className="inline-flex items-center px-6 py-3 bg-white/60 backdrop-blur-sm border border-[#06b6d4]/30 rounded-full text-sm font-medium transform hover:scale-105 transition-all duration-300 hover:shadow-lg">
              <GraduationCap className="w-4 h-4 mr-2" />
              Industry Recognized
            </span>
          </div>
        </section>

        {/* Courses Section */}
        <section className="max-w-6xl mx-auto py-16 relative z-10">
          <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {courses.map((course, index) => {
              const IconComponent = course.icon
              return (
                <div
                  key={course.id}
                  className="group bg-white/60 backdrop-blur-xl p-8 rounded-3xl shadow-lg border border-white/20 transform hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:bg-white/80 relative overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0f766e]/5 via-[#7c3aed]/5 to-[#06b6d4]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>

                  <div className="relative z-10">
                    {/* Course Icon */}
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#0f766e] to-[#7c3aed] rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>

                    {/* Course Title */}
                    <h2 className="text-xl font-bold mb-4 group-hover:text-[#0f766e] transition-colors duration-300">
                      {course.title}
                    </h2>

                    {/* Course Description */}
                    <p className="text-gray-600 mb-6 leading-relaxed">{course.description}</p>

                    {/* Course Details */}
                    <div className="flex items-center justify-around mb-6 text-sm">
                      <div className="flex items-center space-x-1 text-[#7c3aed]">
                        <Clock className="w-4 h-4" />
                        <span>{course.duration}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-[#06b6d4]">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-xs">{course.level}</span>
                      </div>
                    </div>

                    {/* Course Features */}
                    <div className="space-y-2 mb-6">
                      {course.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Enroll Button */}
                    <Link
                      to="/register"
                      className="block w-full text-center bg-gradient-to-r from-[#0f766e] to-[#7c3aed] hover:from-[#06b6d4] hover:to-[#8b5cf6] text-white px-6 py-3 rounded-2xl font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(6,182,212,0.5)]"
                    >
                      Enroll Now
                    </Link>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16">
            <div className="bg-white/60 backdrop-blur-xl p-8 rounded-3xl shadow-lg border border-white/20 max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">
                <span className="bg-gradient-to-r from-[#0f766e] via-[#7c3aed] to-[#0f766e] bg-clip-text text-transparent">
                  Ready to Start Your Journey?
                </span>
              </h3>
              <p className="text-gray-600 mb-6">
                Join thousands of successful engineers who have transformed their careers with our expert training
                programs.
              </p>
              <Link
                to="/register"
                className="inline-block bg-gradient-to-r from-[#0f766e] to-[#7c3aed] hover:from-[#06b6d4] hover:to-[#8b5cf6] text-white px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(6,182,212,0.5)]"
              >
                Register for a Course
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white text-sm py-10 relative overflow-hidden">
        <div className="relative z-10">
          <div className="container mx-auto">
            <div className="flex flex-wrap justify-center gap-4 text-center mb-6">
              {footerLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.to}
                  className="px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gradient-to-r hover:from-[#06b6d4] hover:to-[#7c3aed] transition-all duration-300 border border-gray-600 rounded-lg hover:border-[#06b6d4] hover:shadow-[0_0_15px_rgba(6,182,212,0.5)] relative overflow-hidden group transform hover:scale-105 hover:-translate-y-1"
                >
                  <span className="relative z-10">{link.label}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#06b6d4]/20 to-[#7c3aed]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#06b6d4] group-hover:w-full transition-all duration-300"></div>
                </Link>
              ))}
            </div>
            <div className="text-center">
              <p className="text-gray-400 mb-2">
                &copy; 2025 StructuralGuru - GURURAYAR ASSOCIATES. All Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Chat Widget */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          className="group bg-gradient-to-r from-[#0f766e] to-[#7c3aed] text-white p-4 rounded-full shadow-2xl hover:shadow-[0_0_25px_rgba(6,182,212,0.6)] transition-all duration-300 hover:scale-110 hover:-translate-y-1 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#06b6d4]/30 to-[#8b5cf6]/30 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300"></div>
          <div className="relative z-10">
            {isChatOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
          </div>
        </button>
      </div>

      {/* Chat Box */}
      <div
        className={`fixed bottom-24 right-6 w-80 bg-white/95 backdrop-blur-xl border border-gray-200/20 rounded-3xl shadow-2xl transition-all duration-500 z-40 ${
          isChatOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 translate-y-8 invisible"
        }`}
      >
        <div className="p-6">
          <h3 className="text-xl font-semibold mb-6 text-center">
            <span className="bg-gradient-to-r from-[#0f766e] to-[#7c3aed] bg-clip-text text-transparent">
              Customer Support
            </span>
          </h3>
          <form onSubmit={handleChatSubmit} className="space-y-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Your Name"
                className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300"
                required
              />
            </div>
            <div className="relative">
              <input
                type="email"
                placeholder="Your Email"
                className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300"
                required
              />
            </div>
            <div className="relative">
              <textarea
                placeholder="How can I help you?"
                rows={3}
                className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#0f766e]/20 focus:border-[#0f766e]/30 transition-all duration-300 resize-none"
                required
              ></textarea>
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-[#0f766e] to-[#7c3aed] text-white py-3 rounded-2xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(6,182,212,0.5)]"
            >
              Customer Care for Support
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
